import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

type ModalProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  kicker?: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
};

const sizes = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

export function Modal({ open, onClose, title, kicker, description, children, footer, size = 'md' }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-bg/80 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />

      {/* Panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className={cn(
          'relative w-full bg-bg-panel border border-border-strong rounded-sm shadow-raised',
          'animate-slide-up max-h-[90vh] flex flex-col',
          sizes[size],
        )}
      >
        {/* Brass corner accent */}
        <div className="absolute top-0 left-0 w-12 h-px bg-brass-400" />
        <div className="absolute top-0 left-0 h-12 w-px bg-brass-400" />

        {/* Header */}
        <header className="flex items-start justify-between px-7 pt-7 pb-5 shrink-0">
          <div className="flex-1 pr-4">
            {kicker && <div className="kicker mb-2">{kicker}</div>}
            <h2 id="modal-title" className="font-display text-2xl text-text leading-tight">
              {title}
            </h2>
            {description && (
              <p className="mt-2 text-sm text-text-muted leading-relaxed">{description}</p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 -m-1.5 text-text-dim hover:text-text hover:bg-bg-hover rounded-sm transition-colors"
          >
            <X size={18} />
          </button>
        </header>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-7 pb-2">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <footer className="px-7 py-5 border-t border-border flex items-center justify-end gap-3 shrink-0 mt-2">
            {footer}
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
}
